import type { NativeParticipantKind } from '@ai-meeting/shared'

export interface TeamsAudioParticipant {
  providerParticipantId: string
  providerSessionId: string
  displayName: string
  kind: NativeParticipantKind
}

export interface TeamsAudioFrame {
  streamId: number
  timestampMs: number
  pcm16: Uint8Array
  participant: TeamsAudioParticipant
}

interface GatewayAudioClientOptions {
  url: string
  onAudio: (frame: TeamsAudioFrame) => void
  onParticipant?: (participant: TeamsAudioParticipant) => void
  onError?: (error: Error) => void
  onClose?: () => void
  createWebSocket?: (url: string) => WebSocket
}

interface ParticipantControlMessage extends TeamsAudioParticipant {
  event: 'participant'
  streamId: number
}

const AUDIO_PROTOCOL_VERSION = 1
const AUDIO_HEADER_LENGTH = 13
const PARTICIPANT_KINDS = new Set<NativeParticipantKind>([
  'SIGNED_IN',
  'GUEST',
  'PHONE',
  'ROOM',
  'UNKNOWN',
])

function parseParticipantControl(data: string): ParticipantControlMessage | null {
  let value: unknown
  try {
    value = JSON.parse(data)
  } catch {
    return null
  }
  if (!value || typeof value !== 'object') return null
  const message = value as Record<string, unknown>
  if (
    message.event !== 'participant' ||
    typeof message.streamId !== 'number' ||
    !Number.isInteger(message.streamId) ||
    message.streamId < 0 ||
    typeof message.providerParticipantId !== 'string' ||
    typeof message.providerSessionId !== 'string' ||
    typeof message.displayName !== 'string' ||
    typeof message.kind !== 'string' ||
    !PARTICIPANT_KINDS.has(message.kind as NativeParticipantKind)
  ) {
    return null
  }
  return message as unknown as ParticipantControlMessage
}

/** Receives participant-tagged PCM directly from the Teams media gateway. */
export class GatewayAudioClient {
  private readonly options: GatewayAudioClientOptions
  private readonly participantsByStream = new Map<number, TeamsAudioParticipant>()
  private socket: WebSocket | null = null
  private closing = false

  constructor(options: GatewayAudioClientOptions) {
    this.options = options
  }

  connect(): void {
    if (this.socket) return
    this.closing = false
    const socket = (this.options.createWebSocket ?? ((url) => new WebSocket(url)))(this.options.url)
    socket.binaryType = 'arraybuffer'
    this.socket = socket

    socket.onmessage = (message) => {
      if (typeof message.data === 'string') {
        const control = parseParticipantControl(message.data)
        if (!control) return
        const participant: TeamsAudioParticipant = {
          providerParticipantId: control.providerParticipantId,
          providerSessionId: control.providerSessionId,
          displayName: control.displayName,
          kind: control.kind,
        }
        this.participantsByStream.set(control.streamId, participant)
        this.options.onParticipant?.(participant)
        return
      }

      if (!(message.data instanceof ArrayBuffer) || message.data.byteLength < AUDIO_HEADER_LENGTH) {
        return
      }
      const view = new DataView(message.data)
      if (view.getUint8(0) !== AUDIO_PROTOCOL_VERSION) return
      const streamId = view.getUint32(1, true)
      const participant = this.participantsByStream.get(streamId)
      if (!participant) return
      const timestampMs = Number(view.getBigInt64(5, true))
      if (!Number.isSafeInteger(timestampMs)) return

      this.options.onAudio({
        streamId,
        timestampMs,
        participant,
        pcm16: new Uint8Array(message.data.slice(AUDIO_HEADER_LENGTH)),
      })
    }
    socket.onerror = () => this.options.onError?.(new Error('Teams gateway audio WebSocket error'))
    socket.onclose = () => {
      if (this.socket === socket) this.socket = null
      this.participantsByStream.clear()
      if (!this.closing) this.options.onClose?.()
      this.closing = false
    }
  }

  close(): void {
    const socket = this.socket
    this.socket = null
    this.closing = true
    this.participantsByStream.clear()
    socket?.close()
  }
}
